import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiBold, FiItalic, FiUnderline, 
  FiList, FiHash 
} = FiIcons;

const MenuButton = ({ onClick, isActive, icon, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-2 rounded-lg transition-all ${
      isActive 
        ? 'bg-stone-900 text-white shadow-md' 
        : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'
    }`}
  >
    <SafeIcon icon={icon} className="text-sm" />
  </button>
);

const RichTextEditor = ({ value, onChange, placeholder, className = "" }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
      }),
      Underline,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4 text-stone-900 font-medium`,
      },
    },
  });

  // Ensure content is updated when value prop changes (critical for edit mode)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Only set content if it's actually different to avoid cursor jumping
      // and only if the editor is not currently focused (to avoid interrupting typing)
      // or if the editor is empty (initial load case)
      const currentContent = editor.getHTML();
      if (value !== currentContent && (!editor.isFocused || currentContent === '<p></p>')) {
        editor.commands.setContent(value);
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={`overflow-hidden border border-stone-200 rounded-2xl bg-white focus-within:ring-2 transition-all ${className}`}>
      <div className="flex items-center gap-1 p-2 border-b border-stone-100 bg-stone-50/50">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          icon={FiBold}
          title="Bold"
        />
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          icon={FiItalic}
          title="Italic"
        />
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          icon={FiUnderline}
          title="Underline"
        />
        <div className="w-px h-4 bg-stone-200 mx-1" />
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          icon={FiList}
          title="Unordered List"
        />
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          icon={FiHash}
          title="Ordered List"
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;